import { createFileRoute, Outlet } from '@tanstack/react-router'
import { createContext, useContext, useMemo } from 'react'
import { z } from 'zod'

import { readStoredDisplayToken, storeDisplayToken } from '@/lib/display-token'

// Venue displays and kiosks (docs/adr/0002) never enter the guest LIFF/login
// tree at all — this pathless layout is the entire boundary between the two.
// A display token identifies a screen, not a person, so nothing here calls
// liff.login() or touches Firebase auth.
const displaySearchSchema = z.object({
  dt: z.string().optional(),
})

export const Route = createFileRoute('/e/$eventId/_display')({
  validateSearch: displaySearchSchema,
  component: RouteComponent,
})

const DisplayTokenContext = createContext<string | null>(null)

// useDisplayToken exposes whatever this render resolved — a fresh `?dt=`
// takes priority over anything stored, so re-minting and reopening the URL
// always wins over a stale saved value.
export function useDisplayToken(): string | null {
  return useContext(DisplayTokenContext)
}

function RouteComponent() {
  const { eventId } = Route.useParams()
  const { dt } = Route.useSearch()

  const token = useMemo(() => {
    if (dt) {
      storeDisplayToken(eventId, dt)
      return dt
    }
    // Deliberately scoped to THIS eventId — see lib/display-token.ts.
    // A token stored for a different event is not read here even if one
    // exists in localStorage, which is what stops a projector re-opened at
    // a different event's URL from silently authenticating as the old one.
    return readStoredDisplayToken(eventId)
  }, [eventId, dt])

  return (
    <DisplayTokenContext.Provider value={token}>
      <Outlet />
    </DisplayTokenContext.Provider>
  )
}
